/* LUFFKA — "One evening" homepage (sections/luffka-home-evening, -morning,
   -shelf). Ported from the owner-approved prototype
   docs/concepts/homepage-concept.html.

   - The margin clock lists every [data-lfk-moment] on the page and runs
     minute by minute between them; its times jump to each moment.
   - The room's colour (data-lfk-room on each moment) holds through a moment
     and changes in the gap between two, flat colour only; text turns ivory
     on dark rooms.
   - One motion idea per moment (settle / breathe / brighten), and the
     shower peak: photo opens to full screen, a beat of dark, the scroll
     turns the lamp on, the water light fills the room, then three clips.
   - Reduced motion or no GSAP: every moment shows in its finished state.
   Look: assets/luffka.css (HOME EVENING). */
(() => {
  const home = document.querySelector('[data-lfk-home]');
  if (!home) return;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desk = () => innerWidth > 860;

  const story = $('[data-lfk-home-story]', home);
  const moments = $$('[data-lfk-moment]');
  const clock = $('[data-lfk-home-clock]', home);
  const now = $('[data-lfk-home-now]', clock);
  const list = $('[data-lfk-home-list]', clock);
  const dot = $('[data-lfk-home-dot]', clock);
  const morning = $('[data-lfk-home-morning]');
  const last = morning || moments[moments.length - 1];
  if (!moments.length) return;

  // ---- the clock's list, one entry per moment
  const items = moments.map((m) => {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.innerHTML = '<small></small><span></span>';
    btn.querySelector('small').textContent = m.dataset.lfkLabel || '';
    btn.querySelector('span').textContent = m.dataset.lfkMoment;
    btn.addEventListener('click', () => scrollTo({ top: anchorY(m, true), behavior: reduce ? 'auto' : 'smooth' }));
    li.append(btn);
    list.append(li);
    return li;
  });

  // ---- colour and time, read from the scroll position
  const IVORY = [250, 246, 236], INK = [46, 38, 32], SEC = [107, 96, 85], SEC_D = [190, 178, 162], LINE = [232, 223, 208], LINE_D = [74, 64, 56];
  const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
  const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const smooth = (v) => v * v * (3 - 2 * v);
  const minutes = (t) => { const [h, m] = String(t).split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const top = (el) => el.getBoundingClientRect().top + scrollY;
  // where a moment "is": its pinned screen's middle (or a fraction of it); a plain section's top
  function anchorY(m, jump) {
    if (m.matches('.lfk-home-moment, .lfk-home-peak') && m.offsetHeight > innerHeight * 1.2) {
      const at = jump ? .5 : (+m.dataset.lfkAt || .5);
      return top(m) + (m.offsetHeight - innerHeight) * at;
    }
    return top(m) - (jump ? 0 : innerHeight * .25);
  }
  let anchors = [];
  function measure() {
    let mins = 0, lastRoom = IVORY;
    anchors = moments.map((m) => {
      let t = minutes(m.dataset.lfkMoment);
      while (t < mins) t += 1440; // past midnight: 22:30 -> 07:00 runs forward
      mins = t;
      const room = m.dataset.lfkRoom ? m.dataset.lfkRoom.split(',').map(Number) : lastRoom;
      lastRoom = room;
      return { y: anchorY(m), min: t, bg: room };
    });
  }
  const fmt = (m) => { m = Math.round(m) % 1440; return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'); };

  function frame() {
    const y = scrollY;
    let i = 0; while (i < anchors.length - 1 && y > anchors[i + 1].y) i++;
    const a = anchors[i], b = anchors[Math.min(i + 1, anchors.length - 1)];
    const p = b === a ? (y > a.y ? 1 : 0) : clamp((y - a.y) / (b.y - a.y));
    const before = y < anchors[0].y;
    const bg = before ? anchors[0].bg : mix(a.bg, b.bg, smooth(clamp((p - .3) / .4)));
    const lum = (bg[0] * .3 + bg[1] * .59 + bg[2] * .11) / 255;
    const tf = clamp((.62 - lum) / .2);
    const st = story.style;
    st.setProperty('--lfk-home-bg', rgb(bg));
    st.setProperty('--lfk-home-fg', rgb(mix(INK, IVORY, tf)));
    st.setProperty('--lfk-home-fg2', rgb(mix(SEC, SEC_D, tf)));
    st.setProperty('--lfk-home-rule', rgb(mix(LINE, LINE_D, tf)));
    now.textContent = fmt(before ? anchors[0].min : a.min + (b.min - a.min) * p);
    const cur = before ? 0 : (p > .5 ? Math.min(i + 1, anchors.length - 1) : i);
    items.forEach((li, n) => li.classList.toggle('is-current', n === cur));
    dot.style.transform = `translateY(${items[cur].offsetTop + items[cur].offsetHeight / 2 - 3}px)`;
    const mid = y + innerHeight / 2;
    const inMorning = morning && mid > top(morning);
    const clk = inMorning ? IVORY : mix(INK, IVORY, tf);
    clock.style.setProperty('--lfk-home-clk', rgb(clk));
    clock.style.setProperty('--lfk-home-clk-bg', inMorning ? '#670626' : rgb(bg));
    const storyTop = top(story), lastEnd = top(last) + last.offsetHeight * (morning ? .62 : 1);
    clock.classList.toggle('is-on', mid > storyTop && mid < lastEnd);
    // the site header slides away while the story fills the screen
    root.classList.toggle('lfk-eve-immersive', y > storyTop - 80 && mid < (morning ? top(morning) : lastEnd));
  }
  // phones: the clock sits just under the sticky header
  function placeClock() {
    const hdr = $('.lfk-hdr-section') || $('.section-header');
    const h = hdr ? Math.max(0, hdr.getBoundingClientRect().bottom) : 0;
    clock.style.setProperty('--lfk-home-clock-top', (h + 12) + 'px');
  }
  root.classList.add('lfk-eve-page');
  measure(); frame(); placeClock();
  let raf = 0;
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; frame(); if (!desk()) placeClock(); }); }, { passive: true });
  addEventListener('resize', () => { measure(); frame(); placeClock(); });
  addEventListener('load', () => { measure(); frame(); placeClock(); });

  // ---- reviews: arrows and dots
  const rev = $('[data-lfk-home-reviews]');
  if (rev) {
    const dots = $$('[data-lfk-home-dots] i');
    const step = () => (rev.firstElementChild ? rev.firstElementChild.offsetWidth + 18 : 300);
    const prev = $('[data-lfk-home-prev]'), next = $('[data-lfk-home-next]');
    if (prev) prev.addEventListener('click', () => rev.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => rev.scrollBy({ left: step(), behavior: 'smooth' }));
    rev.addEventListener('scroll', () => {
      const n = Math.round(rev.scrollLeft / step());
      dots.forEach((d, j) => d.classList.toggle('is-on', j === Math.min(n, dots.length - 1)));
    }, { passive: true });
  }

  // ---- looping clips play only while on screen
  const peak = $('[data-lfk-home-peak]');
  if (!reduce) {
    const io = new IntersectionObserver((es) => es.forEach((e) => { e.isIntersecting ? e.target.play().catch(() => {}) : e.target.pause(); }));
    $$('video[loop]', home).forEach((v) => io.observe(v));
  }

  if (!window.gsap || !window.ScrollTrigger || reduce) { root.classList.add('lfk-home-static'); return; }
  gsap.registerPlugin(ScrollTrigger);
  const mm = gsap.matchMedia();
  mm.add({ desk: '(min-width: 861px)', phone: '(max-width: 860px)' }, (ctx) => {
    const D = ctx.conditions.desk;
    // every moment holds the screen (computers and phones), so its motion plays while it is in view
    const enter = (el) => ({ trigger: el, start: 'top 85%', end: 'top top', scrub: .6 });
    const hold = (el) => ({ trigger: el, start: 'top top', end: 'bottom bottom', scrub: .6 });

    // copy rises in once per moment
    $$('.lfk-home-moment [data-lfk-home-copy], [data-lfk-peak-intro] [data-lfk-home-copy], [data-lfk-home-morning] [data-lfk-home-copy]').forEach((c) => {
      gsap.from(c.children, { y: 26, opacity: 0, duration: 1, ease: 'power3.out', stagger: .08, scrollTrigger: { trigger: c, start: 'top 75%', toggleActions: 'play none none reverse' } });
    });

    $$('.lfk-home-moment').forEach((m) => {
      const frameEl = $('[data-lfk-home-frame]', m), inset = $('[data-lfk-home-inset]', m);
      if (m.classList.contains('lfk-home-moment--settle')) {
        // the bag is set down, the bottle follows
        if (frameEl) gsap.fromTo(frameEl, { y: -110, rotate: -5 }, { y: 0, rotate: 0, ease: 'none', scrollTrigger: enter(m) });
        if (inset) gsap.fromTo(inset, { y: 140 }, { y: 0, ease: 'none', scrollTrigger: enter(m) });
      } else if (m.classList.contains('lfk-home-moment--breathe')) {
        // one slow breath
        const words = $$('.lfk-home-breath span', m);
        const tl = gsap.timeline({ scrollTrigger: hold(m) });
        tl.fromTo($('.lfk-home-ring', m), { scale: .78 }, { scale: 1.06, ease: 'sine.inOut', duration: 1 });
        if (words[1]) tl.to(words[0], { opacity: 0, duration: .15 }, .9).to(words[1], { opacity: 1, duration: .15 }, 1);
        tl.to($('.lfk-home-ring', m), { scale: .86, ease: 'sine.inOut', duration: 1 }, 1);
      } else if (m.classList.contains('lfk-home-moment--brighten')) {
        // the room is dark; the photo comes up out of it as the camera leans in
        const img = frameEl && $('img', frameEl);
        if (img) gsap.fromTo(img, { filter: 'brightness(.1)', scale: 1.16 }, { filter: 'brightness(1)', scale: 1, ease: 'none', scrollTrigger: { trigger: m, start: 'top 70%', end: 'bottom bottom', scrub: .8 } });
      }
    });

    // ---- 21:00 the peak
    if (peak) {
      // starts where the other moments keep their photo: computers, a portrait
      // frame in the left column; phones, the photo area above the words
      const rect = () => {
        const W = innerWidth, H = innerHeight;
        if (!D) return { left: 20, top: 64, width: W - 40, height: H * .48 };
        const padL = W >= 1100 ? 40 + 170 : 40; // the stage gutter, plus the margin clock's column
        const colW = (W - padL - 40 - .06 * W) * 1.05 / 2, fh = .72 * H, fw = Math.min(fh * .8, colW);
        return { left: padL + (colW - fw) / 2, top: (H - fh) / 2, width: fw, height: fh };
      };
      const sw = $('[data-lfk-peak-switch] video', peak), scrub = { t: 0 };
      if (sw) {
        // load the clip whole once the peak is near, so it can be scrubbed even where
        // byte ranges are not served; a play/pause wakes the decoder on iPhones
        const load = new IntersectionObserver(([e]) => {
          if (!e.isIntersecting) return;
          load.disconnect();
          fetch(sw.currentSrc || sw.src).then((r) => r.blob()).then((bl) => {
            sw.src = URL.createObjectURL(bl);
            sw.play().then(() => sw.pause()).catch(() => {});
          }).catch(() => {});
        }, { rootMargin: '200% 0px' });
        load.observe(peak);
      }
      gsap.set('[data-lfk-peak-intro]', { opacity: 1 });
      const pk = gsap.timeline({ scrollTrigger: { trigger: peak, start: 'top top', end: 'bottom bottom', scrub: .7, invalidateOnRefresh: true } });
      pk.fromTo('[data-lfk-peak-frame]', { left: () => rect().left, top: () => rect().top, width: () => rect().width, height: () => rect().height, borderRadius: 4 },
        { left: 0, top: 0, width: '100%', height: '100%', borderRadius: 0, duration: .16, ease: 'power2.inOut' }, 0)
        .fromTo('[data-lfk-peak-dark]', { opacity: 0 }, { opacity: .2, duration: .14 }, 0)
        .to('[data-lfk-peak-intro]', { opacity: 0, duration: .05 }, .02)
        // the big light goes off: one beat of dark
        .to('[data-lfk-peak-dark]', { opacity: .97, duration: .05, ease: 'power1.in' }, .18);
      if (sw) {
        // in the dark, a hand reaches for the lamp; the scroll turns it on
        pk.fromTo('[data-lfk-peak-switch]', { opacity: 0, scale: .96 }, { opacity: 1, scale: 1, duration: .04 }, .23)
          .fromTo('[data-lfk-peak-caption]', { opacity: 0 }, { opacity: 1, duration: .04 }, .24)
          .fromTo(scrub, { t: 0 }, { t: 1, duration: .12, ease: 'none', onUpdate: () => { if (sw.readyState > 0 && sw.duration) sw.currentTime = scrub.t * (sw.duration - .05); } }, .25)
          .to('[data-lfk-peak-caption]', { opacity: 0, duration: .03 }, .35)
          .to('[data-lfk-peak-switch]', { opacity: 0, scale: 1.04, duration: .06 }, .38);
      }
      // and the water light fills the room: right after the beat of dark, or
      // after the light switch when the block has that clip
      const lit = sw ? .37 : .28;
      pk.fromTo('.lfk-home-peak__water, .lfk-home-peak__phone', { opacity: 0 }, { opacity: 1, duration: .001 }, lit)
        .to('[data-lfk-peak-dark]', { opacity: .38, duration: .08, ease: 'sine.out' }, lit)
        .fromTo('[data-lfk-peak-title] .lfk-home-when', { opacity: 0 }, { opacity: 1, duration: .04 }, lit + .06)
        .fromTo('[data-lfk-peak-title] .lfk-home-line > span', { yPercent: 105 }, { yPercent: 0, duration: .09, stagger: .03, ease: 'power3.out' }, lit + .06)
        .fromTo('[data-lfk-peak-buy]', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: .07 }, lit + .15);
      if ($('[data-lfk-peak-row]', peak)) {
        // then the people it is for; the description steps aside for them
        const row = sw ? .62 : .5;
        pk.to('[data-lfk-peak-title]', { opacity: 0, y: -40, duration: .06 }, row)
          .to('[data-lfk-peak-buy]', { opacity: 0, y: 30, duration: .05 }, row)
          .to('[data-lfk-peak-dark]', { opacity: .72, duration: .08 }, row)
          .fromTo('.lfk-home-peak__row-title', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: .06 }, row + .04)
          .fromTo('.lfk-home-peak__clip', { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: .08, stagger: .025, ease: 'power3.out' }, row + .05)
          .fromTo('.lfk-home-peak__row-link', { opacity: 0 }, { opacity: 1, duration: .05 }, row + .12);
        if (!D) {
          // phones: the three clips sit in a row that slides across with the scroll
          const clips = $('.lfk-home-peak__clips', peak);
          pk.fromTo(clips, { x: 0 }, { x: () => -Math.max(0, clips.scrollWidth - innerWidth), duration: sw ? .14 : .24, ease: 'none' }, row + .1);
        }
        pk.to('[data-lfk-peak-row]', { opacity: 0, duration: .06 }, .88);
      } else {
        pk.to(['[data-lfk-peak-title]', '[data-lfk-peak-buy]'], { opacity: 0, duration: .06 }, .88);
      }
      // the room settles into night for bedtime
      pk.to('[data-lfk-peak-dark]', { opacity: 1, duration: .08 }, .88);
    }

    // the grid settles in as a set
    const grid = $('[data-lfk-home-grid]');
    if (grid) gsap.from(grid.children, { y: 30, opacity: 0, duration: .9, ease: 'power3.out', stagger: .07, scrollTrigger: { trigger: grid, start: 'top 80%' } });
  });
  addEventListener('load', () => { ScrollTrigger.refresh(); measure(); frame(); });
})();
